import { DomainError } from '../errors/domain-error';

export const COMMUNITY_REPORT_ESCALATION_THRESHOLD_DEFAULT = 3;
export const COMMUNITY_REPORT_ESCALATION_THRESHOLD_MAX = 20;
export const COMMUNITY_MODERATION_APPEAL_MESSAGE_MAX = 2000;

export const CommunityMemberInteractionEffect = {
  mute: {
    hidePosts: true,
    hideComments: true,
    restrictDms: false,
  },
  block: {
    hidePosts: true,
    hideComments: true,
    restrictDms: true,
  },
} as const;

export function buildCommunityGuidelinesAuditMetadata(rules: string | null | undefined) {
  const text = (rules ?? '').trim();
  return {
    rulesPresent: text.length > 0,
    rulesLength: text.length,
  };
}

export function normalizeCommunityReportEscalationThreshold(
  value: number | null | undefined,
): number {
  if (value == null || !Number.isFinite(value)) {
    return COMMUNITY_REPORT_ESCALATION_THRESHOLD_DEFAULT;
  }
  const normalized = Math.trunc(value);
  if (normalized < 0) return 0;
  if (normalized > COMMUNITY_REPORT_ESCALATION_THRESHOLD_MAX) {
    return COMMUNITY_REPORT_ESCALATION_THRESHOLD_MAX;
  }
  return normalized;
}

export function canSubmitCommunityModerationAppeal(params: {
  membershipStatus?: string | null;
  appealType: 'ban' | 'content_removal';
  targetType?: string | null;
  targetId?: string | null;
  hasPendingAppeal?: boolean;
}): { ok: true } | { ok: false; message: string } {
  if (params.hasPendingAppeal) {
    return { ok: false, message: 'You already have a pending appeal for this community' };
  }

  if (params.appealType === 'ban') {
    if (params.membershipStatus !== 'banned') {
      return { ok: false, message: 'Only banned members can submit a ban appeal' };
    }
    return { ok: true };
  }

  if (!params.targetType || !params.targetId) {
    return { ok: false, message: 'Content appeals require a target post' };
  }

  return { ok: true };
}

export function validateCommunityModerationAppealMessage(
  message: string | null | undefined,
): { ok: true; message: string } | { ok: false; message: string } {
  const trimmed = (message ?? '').trim();
  if (!trimmed) {
    return { ok: false, message: 'Appeal message is required' };
  }
  if (trimmed.length > COMMUNITY_MODERATION_APPEAL_MESSAGE_MAX) {
    return {
      ok: false,
      message: `Appeal message must be ${COMMUNITY_MODERATION_APPEAL_MESSAGE_MAX} characters or fewer`,
    };
  }
  return { ok: true, message: trimmed };
}

export function assertCanResolveCommunityModerationAppeal(
  role: string | null | undefined,
): void {
  if (role !== 'owner' && role !== 'moderator') {
    throw new DomainError('Only community moderators can resolve appeals');
  }
}

export function shouldEscalateCommunityReports(
  openReportCount: number,
  threshold: number,
): boolean {
  return threshold > 0 && openReportCount >= threshold;
}
